// components/AddLayerModal.tsx
import React, { useState } from "react";
import type { MapLayer, WMSParams } from "../types";
import { useDispatch } from "react-redux";
import { addLayer } from "../../store/slices/layerSlice";

interface AddLayerModalProps {
  onAdd: (layer: Omit<MapLayer, "id" | "zIndex">) => void;
  onClose: () => void;
}

type Tab = "xyz" | "wms" | "wmts" | "geojson" | "wfs" | "cog" | "image";

const TAB_LABELS: { id: Tab; label: string; icon: string }[] = [
  { id: "xyz", label: "XYZ Tiles", icon: "🗺" },
  { id: "wms", label: "WMS", icon: "🔲" },
  { id: "wmts", label: "WMTS", icon: "⊞" },
  { id: "geojson", label: "GeoJSON", icon: "{}" },
  { id: "wfs", label: "WFS", icon: "⬡" },
  { id: "cog", label: "COG", icon: "🛰" },
  { id: "image", label: "Image", icon: "🖼" },
];

// Quick-fill presets per tab
const XYZ_PRESETS = [
  { label: "OpenStreetMap", url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" },
  { label: "CartoDB Light", url: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" },
  { label: "CartoDB Dark", url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" },
  { label: "ESRI Satellite", url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" },
  { label: "ESRI Topo", url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}" },
];

const WMS_PRESETS = [
  { label: "NASA GIBS Blue Marble", url: "https://gibs.earthdata.nasa.gov/wms/epsg4326/best/wms.cgi", layers: "BlueMarble_ShadedRelief" },
  { label: "Demo GeoServer", url: "https://ahocevar.com/geoserver/wms", layers: "topp:states" },
];

const GEOJSON_PRESETS = [
  { label: "World Countries", url: "https://raw.githubusercontent.com/datasets/geo-countries/master/data/countries.geojson" },
  { label: "Natural Earth 110m", url: "https://d2ad6b4ur7yvpq.cloudfront.net/naturalearth-3.3.0/ne_110m_land.geojson" },
];

const I = (props: React.InputHTMLAttributes<HTMLInputElement> & { label: string }) => {
  const { label, ...rest } = props;
  return (
    <label
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        fontSize: "10px",
        color: "rgba(255,255,255,0.45)",
        letterSpacing: "1px",
        textTransform: "uppercase",
        fontWeight: 700,
      }}
    >
      {label}
      <input
        {...rest}
        style={{
          background: "rgba(255,255,255,0.06)",
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: "6px",
          padding: "7px 10px",
          fontSize: "12px",
          color: "rgba(255,255,255,0.85)",
          outline: "none",
          fontFamily: "inherit",
          transition: "border-color 0.15s",
          ...rest.style,
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = "rgba(0,212,255,0.5)";
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)";
        }}
      />
    </label>
  );
};

const AddLayerModal: React.FC<AddLayerModalProps> = ({ onAdd, onClose }) => {
  const [tab, setTab] = useState<Tab>("xyz");
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [layers, setLayers] = useState(""); // WMS layers param
  const [format, setFormat] = useState("image/png");
  const [tileMatrix, setTileMatrix] = useState("EPSG:3857");
  const [wmtsLayer, setWmtsLayer] = useState("");
  const [geojsonUrl, setGeojsonUrl] = useState("");
  const [geojsonText, setGeojsonText] = useState("");
  const [useText, setUseText] = useState(false);
  const [wfsUrl, setWfsUrl] = useState("");
  const [wfsType, setWfsType] = useState("");
  const [cogUrl, setCogUrl] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [imageCoords, setImageCoords] = useState("");
  const [error, setError] = useState("");
  
  const dispatch = useDispatch();

  const reset = () => {
    setName("");
    setUrl("");
    setLayers("");
    setFormat("image/png");
    setTileMatrix("EPSG:3857");
    setWmtsLayer("");
    setGeojsonUrl("");
    setGeojsonText("");
    setUseText(false);
    setWfsUrl("");
    setWfsType("");
    setError("");
  };

  const handleAdd = () => {
    setError("");
    if (!name.trim()) {
      setError("Layer name is required");
      return;
    }

    const base = { id: `layer-${Date.now()}`, name: name.trim(), visible: true, visibility: true, opacity: 1, zIndex: 0 };
    if (tab === 'xyz') {
      if (!url.trim()) { setError('URL is required'); return; }
      dispatch(addLayer({ ...base, type: 'xyz', url: url.trim() }));
    } else if (tab === 'wms') {
      if (!url.trim() || !layers.trim()) { setError('URL and Layers are required'); return; }
      dispatch(addLayer({ ...base, type: 'wms', url: url.trim(),
        wmsParams: { layers: layers.trim(), format, transparent: true, version: '1.1.1' } }));
    } else if (tab === 'wmts') {
      if (!url.trim() || !wmtsLayer.trim()) { setError('URL and Layer are required'); return; }
      dispatch(addLayer({ ...base, type: 'wmts', url: url.trim(),
        wmtsParams: { layer: wmtsLayer.trim(), tileMatrixSet: tileMatrix, format } }));
    } else if (tab === 'geojson') {
      if (useText) {
        if (!geojsonText.trim()) { setError('GeoJSON text is required'); return; }
        try {
          const data = JSON.parse(geojsonText.trim());
          dispatch(addLayer({ ...base, type: 'geojson', geojsonData: data }));
        } catch { setError('Invalid JSON'); return; }
      } else {
        if (!geojsonUrl.trim()) { setError('URL is required'); return; }
        dispatch(addLayer({ ...base, type: 'geojson', geojsonUrl: geojsonUrl.trim() }));
      }
    } else if (tab === 'wfs') {
      if (!wfsUrl.trim() || !wfsType.trim()) { setError('URL and Type Name are required'); return; }
      dispatch(addLayer({ ...base, type: 'wfs', url: wfsUrl.trim(),
        wmsParams: { layers: wfsType.trim() } as WMSParams }));
    } else if (tab === 'cog') {
      dispatch(addLayer({ ...base, type: 'cog', url: cogUrl.trim() }));
    }else if (tab === 'image') {
      dispatch(addLayer({ ...base, type: 'image', url: imageUrl.trim(), imageParams:{
          coordinates: imageCoords.trim() ? JSON.parse(imageCoords.trim()) : undefined,
          urlType: imageUrl.trim().startsWith("data:image") ? "base64" : "url",
      }}));
    }

    reset();
    onClose();
  };

  const btnStyle = (active: boolean): React.CSSProperties => ({
    padding: "6px 12px",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "11px",
    fontFamily: "inherit",
    transition: "all 0.15s",
    outline: "none",
    background: active ? "rgba(0,212,255,0.12)" : "transparent",
    border: `1px solid ${active ? "rgba(0,212,255,0.4)" : "rgba(255,255,255,0.1)"}`,
    color: active ? "#00d4ff" : "rgba(255,255,255,0.5)",
    fontWeight: active ? 700 : 400,
  });

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9000,
        background: "rgba(0,0,0,0.65)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: "#111420",
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: "16px",
          padding: "24px",
          width: "640px",
          maxWidth: "95vw",
          maxHeight: "90vh",
          overflowY: "auto",
          fontFamily: '"JetBrains Mono","Fira Code",monospace',
          boxShadow: "0 24px 64px rgba(0,0,0,0.7)",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <span style={{ fontSize: "14px", fontWeight: 700, color: "rgba(255,255,255,0.85)", letterSpacing: "0.5px" }}>＋ Add Layer</span>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "rgba(255,255,255,0.35)",
              fontSize: "18px",
              lineHeight: 1,
              padding: "2px 6px",
              borderRadius: "4px",
              fontFamily: "inherit",
            }}
          >
            ✕
          </button>
        </div>

        {/* Tab bar */}
        <div style={{ display: "flex", gap: "4px", marginBottom: "20px", flexWrap: "wrap" }}>
          {TAB_LABELS.map((t) => (
            <button
              key={t.id}
              onClick={() => {
                setTab(t.id);
                reset();
              }}
              style={btnStyle(tab === t.id)}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* Layer name (always shown) */}
        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <I label="Layer Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="My Layer" />

          {/* XYZ */}
          {tab === "xyz" && (
            <>
              <div>
                <div
                  style={{
                    fontSize: "9px",
                    color: "rgba(255,255,255,0.3)",
                    letterSpacing: "1px",
                    textTransform: "uppercase",
                    fontWeight: 700,
                    marginBottom: "6px",
                  }}
                >
                  Quick Presets
                </div>
                <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                  {XYZ_PRESETS.map((p) => (
                    <button
                      key={p.label}
                      onClick={() => {
                        setUrl(p.url);
                        setName(p.label);
                      }}
                      style={{ ...btnStyle(false), fontSize: "10px", padding: "4px 8px" }}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              <I
                label="Tile URL ({z}/{x}/{y})"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
            </>
          )}

          {/* WMS */}
          {tab === "wms" && (
            <>
              <div>
                <div
                  style={{
                    fontSize: "9px",
                    color: "rgba(255,255,255,0.3)",
                    letterSpacing: "1px",
                    textTransform: "uppercase",
                    fontWeight: 700,
                    marginBottom: "6px",
                  }}
                >
                  Quick Presets
                </div>
                <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                  {WMS_PRESETS.map((p) => (
                    <button
                      key={p.label}
                      onClick={() => {
                        setUrl(p.url);
                        setLayers(p.layers);
                        setName(p.label);
                      }}
                      style={{ ...btnStyle(false), fontSize: "10px", padding: "4px 8px" }}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              <I label="WMS Service URL" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com/geoserver/wms" />
              <I
                label="Layer Names (comma-separated)"
                value={layers}
                onChange={(e) => setLayers(e.target.value)}
                placeholder="workspace:layer_name"
              />
              <I label="Image Format" value={format} onChange={(e) => setFormat(e.target.value)} placeholder="image/png" />
            </>
          )}

          {/* WMTS */}
          {tab === "wmts" && (
            <>
              <I
                label="WMTS Service URL"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com/geoserver/gwc/service/wmts"
              />
              <I label="Layer Name" value={wmtsLayer} onChange={(e) => setWmtsLayer(e.target.value)} placeholder="workspace:layer_name" />
              <I label="Tile Matrix Set" value={tileMatrix} onChange={(e) => setTileMatrix(e.target.value)} placeholder="EPSG:3857" />
              <I label="Format" value={format} onChange={(e) => setFormat(e.target.value)} placeholder="image/png" />
            </>
          )}

          {/* GeoJSON */}
          {tab === "geojson" && (
            <>
              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <button onClick={() => setUseText(false)} style={btnStyle(!useText)}>
                  From URL
                </button>
                <button onClick={() => setUseText(true)} style={btnStyle(useText)}>
                  Paste JSON
                </button>
              </div>
              {!useText ? (
                <>
                  <div>
                    <div
                      style={{
                        fontSize: "9px",
                        color: "rgba(255,255,255,0.3)",
                        letterSpacing: "1px",
                        textTransform: "uppercase",
                        fontWeight: 700,
                        marginBottom: "6px",
                      }}
                    >
                      Presets
                    </div>
                    <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                      {GEOJSON_PRESETS.map((p) => (
                        <button
                          key={p.label}
                          onClick={() => {
                            setGeojsonUrl(p.url);
                            setName(p.label);
                          }}
                          style={{ ...btnStyle(false), fontSize: "10px", padding: "4px 8px" }}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <I
                    label="GeoJSON URL"
                    value={geojsonUrl}
                    onChange={(e) => setGeojsonUrl(e.target.value)}
                    placeholder="https://example.com/data.geojson"
                  />
                </>
              ) : (
                <label
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    fontSize: "10px",
                    color: "rgba(255,255,255,0.45)",
                    letterSpacing: "1px",
                    textTransform: "uppercase",
                    fontWeight: 700,
                  }}
                >
                  GeoJSON Text
                  <textarea
                    value={geojsonText}
                    onChange={(e) => setGeojsonText(e.target.value)}
                    placeholder='{"type":"FeatureCollection","features":[...]}'
                    rows={6}
                    style={{
                      background: "rgba(255,255,255,0.06)",
                      border: "1px solid rgba(255,255,255,0.12)",
                      borderRadius: "6px",
                      padding: "7px 10px",
                      fontSize: "11px",
                      color: "rgba(255,255,255,0.85)",
                      outline: "none",
                      fontFamily: "inherit",
                      resize: "vertical",
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = "rgba(0,212,255,0.5)";
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)";
                    }}
                  />
                </label>
              )}
            </>
          )}

          {/* WFS */}
          {tab === "wfs" && (
            <>
              <I label="WFS Service URL" value={wfsUrl} onChange={(e) => setWfsUrl(e.target.value)} placeholder="https://example.com/geoserver/wfs" />
              <I label="Type Name" value={wfsType} onChange={(e) => setWfsType(e.target.value)} placeholder="workspace:layer_name" />
              <div style={{ fontSize: "10px", color: "rgba(255,255,255,0.3)", lineHeight: 1.5 }}>
                WFS features will be loaded as GeoJSON via GetFeature request.
              </div>
            </>
          )}

          {/* COG */}
          {tab === "cog" && (
            <>
              <I
                label="COG URL (.tif / .tiff)"
                value={cogUrl}
                onChange={(e) => setCogUrl(e.target.value)}
                placeholder="https://example.com/file.tif"
              />
              <div
                style={{
                  fontSize: "10px",
                  color: "rgba(255,255,255,0.3)",
                  lineHeight: 1.6,
                  background: "rgba(255,255,255,0.04)",
                  borderRadius: "6px",
                  padding: "8px 10px",
                }}
              >
                <strong style={{ color: "rgba(255,255,255,0.5)" }}>Cloud Optimized GeoTIFF</strong>
                <br />
                Rendered client-side using geotiff.js. Supports RGB, single-band, and multi-band COGs. Large files may take a moment to load.
              </div>
            </>
          )}
          {/* Satellite Image */}
          {tab === "image" && (
            <>
              <I
                label="Image URL / Base64 URL (.jpg / .png)"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://example.com/file.png"
              />
              <label
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    fontSize: "10px",
                    color: "rgba(255,255,255,0.45)",
                    letterSpacing: "1px",
                    textTransform: "uppercase",
                    fontWeight: 700,
                  }}
                >
                Coordinates
                <textarea
                  value={imageCoords}
                  onChange={(e) => setImageCoords(e.target.value)}
                  placeholder="[[102.81977324404863,22.48321260808015],[102.81977324404863,16.386664525602086],[109.23173904120898,16.386664525602086],[109.23173904120898,22.48321260808015],[102.81977324404863,22.48321260808015]]"
                  rows={4}
                  style={{
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: "6px",
                    padding: "7px 10px",
                    fontSize: "11px",
                    color: "rgba(255,255,255,0.85)",
                    outline: "none",
                    fontFamily: "inherit",
                    resize: "vertical",
                  }} 
                />
                </label>
              <div
                style={{
                  fontSize: "10px",
                  color: "rgba(255,255,255,0.3)",
                  lineHeight: 1.6,
                  background: "rgba(255,255,255,0.04)",
                  borderRadius: "6px",
                  padding: "8px 10px",
                }}
              >
                <strong style={{ color: "rgba(255,255,255,0.5)" }}>Satellite Image</strong>
                <br />
                Rendered client-side. The image should have georeference information (e.g. world file) or be accompanied by metadata specifying its bounds for correct placement on the map.
              </div>
            </>
          )}

          {/* Error */}
          {error && (
            <div
              style={{
                color: "#ff6b6b",
                fontSize: "11px",
                padding: "6px 10px",
                background: "rgba(255,80,80,0.08)",
                borderRadius: "6px",
                border: "1px solid rgba(255,80,80,0.2)",
              }}
            >
              ⚠ {error}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "4px" }}>
            <button
              onClick={onClose}
              style={{
                padding: "8px 16px",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "12px",
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.12)",
                color: "rgba(255,255,255,0.5)",
                fontFamily: "inherit",
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleAdd}
              style={{
                padding: "8px 20px",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "12px",
                background: "rgba(0,212,255,0.15)",
                border: "1px solid rgba(0,212,255,0.4)",
                color: "#00d4ff",
                fontFamily: "inherit",
                fontWeight: 700,
              }}
            >
              Add Layer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddLayerModal;
